import type { Session, SupabaseClient } from '@supabase/supabase-js'
import { authClient, sharedAuthClient } from './supabase'

// Which Navo24 session this tab acts on, while the site moves from its dedicated identity project onto the
// SHARED project (see docs/shared-sign-in.md). Two sessions can exist in one browser during the move:
//   - shared:   written by every NEW sign-in (magic link, OAuth) and by the hub hand-off (#sso=)
//   - identity: written by the old sign-in; still READ so the release signs nobody out
// Rule: the shared session wins; the identity session is used only when there is no shared one. With the
// feature flag off sharedAuthClient is null, so the answer is always the identity session, as before.

export type SessionSource = 'shared' | 'identity'

export interface ActiveSession {
  source: SessionSource
  session: Session
}

/** Pure: which of the two sessions this tab acts on. Shared wins; null when signed out of both. */
export function pickSession(
  shared: Session | null | undefined,
  identity: Session | null | undefined
): ActiveSession | null {
  if (shared) return { source: 'shared', session: shared }
  if (identity) return { source: 'identity', session: identity }
  return null
}

/** The client that owns a session of the given source. */
export function clientFor(source: SessionSource): SupabaseClient {
  return source === 'shared' && sharedAuthClient ? sharedAuthClient : authClient
}

async function readSession(client: SupabaseClient | null): Promise<Session | null> {
  if (!client) return null
  try {
    return (await client.auth.getSession()).data.session
  } catch {
    return null
  }
}

/** The session this tab acts on right now. Never throws. */
export async function getActiveSession(): Promise<ActiveSession | null> {
  const shared = await readSession(sharedAuthClient)
  // sameProject staging: sharedAuthClient IS authClient, so a second read would be the same session.
  const identity = sharedAuthClient === authClient ? null : await readSession(authClient)
  return pickSession(shared, identity)
}

/** The client behind the active session (identity client when signed out, as today). */
export async function getActiveClient(): Promise<SupabaseClient> {
  const a = await getActiveSession()
  return a ? clientFor(a.source) : authClient
}

/** Bearer token for the API: whichever session is active. The API verifies both projects' tokens. */
export async function getAccessToken(): Promise<string | null> {
  return (await getActiveSession())?.session.access_token ?? null
}
